export interface CognitoNameAttributes {
  name?: string | null;
  given_name?: string | null;
  family_name?: string | null;
}

/**
 * Builds the user's full name from the Cognito user attributes (AB#69654 / AB#74839).
 *
 * Identity providers fill the OIDC attributes differently:
 *  - some send the full display name in `name` and also `family_name` (e.g. BMS/VExMO: name = "John Smith", family_name = "Smith");
 *  - others send only the first name(s) in `name` and the surname in `family_name`
 *    (e.g. Gerdau: name = "Francisco Jose", family_name = "Sousa Junior").
 *
 * `family_name` is therefore appended only when it is not already part of `name`
 * (whole-word, case- and accent-insensitive comparison), so the surname is neither lost nor duplicated.
 */
export function buildCognitoFullName(attributes: CognitoNameAttributes | null | undefined): string {
  const name = normalizeSpaces(attributes?.name);
  const givenName = normalizeSpaces(attributes?.given_name);
  const familyName = normalizeSpaces(attributes?.family_name);

  if (!name) return [givenName, familyName].filter(Boolean).join(' ');
  if (!familyName) return name;

  return toComparable(name).includes(toComparable(familyName)) ? name : `${name} ${familyName}`;
}

function normalizeSpaces(value: string | null | undefined): string {
  return (value ?? '').replace(/\s+/g, ' ').trim();
}

function toComparable(value: string): string {
  const withoutAccents = value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  return ` ${withoutAccents.toLowerCase()} `;
}
